import React, { useState } from "react";
import {
  useDisclosure,
  Flex,
  Box,
  Grid,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Text,
  Button,
} from "@chakra-ui/react";
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

interface CustomDatePickerProps {
  onChange: (value: string) => void;
  value: string;
  pickerType?: "month" | "year" | "day";
  placeholder?: string;
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  onChange,
  value,
  pickerType = "month",
  placeholder = "Select date",
}) => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [currentDate, setCurrentDate] = useState(new Date());

  const handlePrevious = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      if (pickerType === "year") {
        newDate.setFullYear(newDate.getFullYear() - 12);
      } else {
        newDate.setFullYear(newDate.getFullYear() - 1);
      }
      return newDate;
    });
  };

  const handleNext = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      if (pickerType === "year") {
        newDate.setFullYear(newDate.getFullYear() + 12);
      } else {
        newDate.setFullYear(newDate.getFullYear() + 1);
      }
      return newDate;
    });
  };

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    onClose();
  };

  const renderContent = () => {
    switch (pickerType) {
      case "year":
        return renderYears();
      case "day":
        return renderDays();
      case "month":
      default:
        return renderMonths();
    }
  };

  const renderMonths = () => (
    <Grid templateColumns="repeat(3, 1fr)" gap={2}>
      {months.map((month, index) => (
        <Button
          key={month}
          onClick={() =>
            handleSelect(
              `${currentDate.getFullYear()}-${String(index + 1).padStart(
                2,
                "0"
              )}`
            )
          }
          variant={"ghost"}
          borderRadius={8}
          size="sm"
        >
          {month}
        </Button>
      ))}
    </Grid>
  );

  const renderYears = () => {
    const startYear = Math.floor(currentDate.getFullYear() / 12) * 12;
    return (
      <Grid templateColumns="repeat(3, 1fr)" gap={2}>
        {Array.from({ length: 12 }, (_, i) => startYear + i).map((year) => (
          <Button
            key={year}
            onClick={() => handleSelect(year.toString())}
            size="sm"
            variant={"ghost"}
            borderRadius={8}
          >
            {year}
          </Button>
        ))}
      </Grid>
    );
  };

  const renderDays = () => {
    const daysInMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0
    ).getDate();
    const firstDayOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    ).getDay();

    return (
      <Grid templateColumns="repeat(7, 1fr)" gap={1}>
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
          <Text key={day} textAlign="center" fontWeight="bold">
            {day}
          </Text>
        ))}
        {Array.from({ length: firstDayOfMonth }, (_, i) => (
          <Box key={`empty-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => (
          <Button
            key={i + 1}
            onClick={() =>
              handleSelect(
                `${currentDate.getFullYear()}-${String(
                  currentDate.getMonth() + 1
                ).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`
              )
            }
            variant={"ghost"}
            borderRadius={8}
            size="sm"
          >
            {i + 1}
          </Button>
        ))}
      </Grid>
    );
  };

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start">
      {/* @ts-ignore */}
      <PopoverTrigger>
        <Button
          variant={"ghost"}
          borderRadius={8}
          textAlign="left"
          onClick={onToggle}
          width="100%"
          justifyContent={"flex-start"}
        >
          <Text
            fontWeight={value ? "bold" : "normal"}
            flex={1}
            textDecoration="underline"
          >
            {value || placeholder}
          </Text>
          <CalendarIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent width="240px" borderRadius={8} _focus={{}}>
        <PopoverBody>
          <Flex justifyContent="space-between" mb={2}>
            <Button
              size="sm"
              onClick={handlePrevious}
              variant={"ghost"}
              borderRadius={8}
            >
              <ChevronLeftIcon />
            </Button>
            <Text fontWeight="bold">
              {pickerType === "year"
                ? `${Math.floor(currentDate.getFullYear() / 12) * 12} - ${
                    Math.floor(currentDate.getFullYear() / 12) * 12 + 11
                  }`
                : currentDate.getFullYear()}
            </Text>
            <Button
              size="sm"
              onClick={handleNext}
              variant={"ghost"}
              borderRadius={8}
            >
              <ChevronRightIcon />
            </Button>
          </Flex>
          {renderContent()}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default CustomDatePicker;
